import React, { useContext, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Platform, ScrollView } from 'react-native';
import * as base64ToBlob from 'b64-to-blob';
import {
  ActivityIndicator,
  Button, Dialog, Portal,
  IconButton,
} from 'react-native-paper';
import { getDocumentAsync } from 'expo-document-picker';
import {
  Box, VStack, HStack, Text,
} from 'native-base';
import { FindManyGlobalFiles, FindOneGlobalFile } from '../../../gql/file/query';
import { AuthContext } from '../../../context/AuthContext';
import { ROLE_NAMES } from '../../../utils/constant';
import { ModalContext } from '../../../context/ModalContext';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import {
  DeleteOneFileMutation,
  UploadOneFileMutation,
} from '../../../gql/user/mutation';
import { getFileType } from '../../../utils/utils';
import DocumentViewerModal from '../../../modals/DocumentViewer/DocumentViewerModal';
import DocumentEditorModal from '../../../modals/DocumentEditor/DocumentEditorModal';

function GeneralTab() {
  const { authState } = useContext(AuthContext);
  const {
    displayDocumentViewerModal,
    toggleDocumentViewerModal,
    displayConfirmationModal,
    toggleConfirmationModal,
    displayDocumentEditorModal,
    toggleDocumentEditorModal,
  } = useContext(ModalContext);

  const [isLoading, setIsLoading] = useState(false);

  const { data, refetch } = useQuery(FindManyGlobalFiles);
  const files = data?.findManyGlobalFiles;
  const [displayFileTypeModal, setDisplayFileTypeModal] = useState(false);
  const [addOneFile] = useMutation(UploadOneFileMutation);
  const [deleteOneFile] = useMutation(DeleteOneFileMutation);
  const [loadFile] = useLazyQuery(
    FindOneGlobalFile,
    {
      fetchPolicy: 'network-only',
    },
  );

  const viewFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        fileId,
      },
    });
    const base64file = fileData?.data?.findOneGlobalFile?.file;
    toggleDocumentViewerModal({
      fileId, filename, isWeb: Platform.OS === 'web', file: base64file,
    });
    setIsLoading(false);
  };

  const editFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        fileId,
      },
    });
    const base64file = fileData?.data?.findOneGlobalFile?.file;
    toggleDocumentEditorModal({
      fileId, filename, isWeb: Platform.OS === 'web', file: base64file,
    });
    setIsLoading(false);
  };

  const downloadFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        fileId,
      },
    });
    const base64file = fileData.data.findOneGlobalFile.file;
    if (Platform.OS === 'web') {
      const type = getFileType(base64file);
      const cleanedBase64 = base64file.split(',')[1];
      // generate a blob, then a file and then save the file.
      const blob = base64ToBlob(cleanedBase64, type);
      const file = new File([blob], filename, { type });

      import('file-saver').then((fs) => {
        fs.default.saveAs(file);
      });
    } else {
      const FileSystem = React.lazy(() => import('expo-file-system'));
      FileSystem.writeAsStringAsync(filename, base64file, { encoding: 'base64' });
    }
    setIsLoading(false);
  };

  const uploadFile = async () => {
    const result = await getDocumentAsync({
      type: ['application/pdf', 'image/jpeg', 'application/msword'],
      copyToCacheDirectory: true,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      let base64File = asset.uri;
      if (Platform.OS !== 'web') {
        const FileSystem = React.lazy(() => import('expo-file-system'));
        // For native platforms, asset.uri is a file path
        base64File = await FileSystem.readAsStringAsync(asset.uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        // Add the data URL prefix using asset.mimeType
        base64File = `data:${asset.mimeType};base64,${base64File}`;
      }
      await addOneFile({
        variables: {
          file: base64File,
          name: asset.name,
        },
      });
      refetch();

      setDisplayFileTypeModal(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (fileId) {
      await deleteOneFile({
        variables: {
          fileId,
        },
      });
      refetch();
    }
  };

  return displayDocumentViewerModal.display || displayDocumentEditorModal.display
    ? (
      <>
        { displayDocumentViewerModal.display && <DocumentViewerModal />}
        { displayDocumentEditorModal.display && Platform.OS === 'web' && <DocumentEditorModal />}
      </>
    )
    : (
      <VStack flex={1} p={4} space={4}>
        {displayConfirmationModal.display && <ConfirmationModal />}
        <Portal>
          <Dialog visible={displayFileTypeModal} onDismiss={() => setDisplayFileTypeModal(false)}>
            <Dialog.Title>Téléverser un document</Dialog.Title>
            <Dialog.Actions>
              <Button onPress={() => uploadFile()}>Parcourir</Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>

        <HStack alignSelf="flex-end" space={4}>
          {(authState.role === ROLE_NAMES.SUPERADMIN) && (
          <Button
            onPress={() => setDisplayFileTypeModal(true)}
            mode="contained"
          >
            Ajouter
          </Button>
          )}
        </HStack>

        <ScrollView flex={1}>
          <VStack space={3}>
            {isLoading && <ActivityIndicator animating color="blue" />}
            {files?.slice()
              .sort((a, b) => (a.name > b.name ? 1 : -1))
              .map((f) => (
                <Box
                  key={f.id}
                  bg="white"
                  p={4}
                  borderRadius="md"
                  shadow={1}
                >
                  <VStack space={2}>
                    <VStack>
                      <Text fontWeight="bold" fontSize="md" color="black" isTruncated>
                        {f.name}
                      </Text>
                    </VStack>
                    <HStack space={1} alignSelf="flex-end">
                      <IconButton
                        icon="eye-outline"
                        color="blue.500"
                        size={24}
                        onPress={() => viewFile(f.id, f.name)}
                      />
                      {(authState.role === ROLE_NAMES.SUPERADMIN)
                        && Platform.OS === 'web' && (
                        <IconButton
                          icon="image-edit-outline"
                          color="orange.500"
                          size={24}
                          onPress={() => editFile(f.id, f.name)}
                        />
                      )}
                      <IconButton
                        icon="download-outline"
                        color="green.500"
                        size={24}
                        onPress={() => downloadFile(f.id, f.name)}
                      />
                      {(authState.role === ROLE_NAMES.SUPERADMIN) && (
                        <IconButton
                          icon="delete-outline"
                          color="red.500"
                          size={24}
                          onPress={() => toggleConfirmationModal({
                            title: 'Confirmation',
                            message: 'Voulez-vous vraiment supprimer ce document ?',
                            handleSubmit: () => deleteFile(f.id),
                          })}
                        />
                      )}
                    </HStack>
                  </VStack>
                </Box>
              ))}
          </VStack>
        </ScrollView>
      </VStack>
    );
}

export default GeneralTab;
